import { useEffect, useEffectEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CartResponse,
  syncCustomer,
  recalculateCart,
} from "@/networking/carts.api";
import { logoutCustomer } from "@/networking/customers.api";
import { isApiError } from "@/lib/api-helpers";
import { clearJwtCookies } from "@/lib/jwt-shop-scope";
import { DecodedJWT } from "@/lib/utils";

export default function useSyncCustomer(
  cartToken: string,
  paymentMethodsIncludes: string | null,
  jwt: string | null,
  decodedJwt: DecodedJWT | null,
  cartData: CartResponse | undefined,
) {
  const queryClient = useQueryClient();

  const syncCustomerMutation = useMutation({
    mutationKey: ["sync-customer", cartToken],
    mutationFn: syncCustomer,
    onSuccess: async () => {
      try {
        const recalculated = await recalculateCart(
          cartToken,
          paymentMethodsIncludes,
        );
        await queryClient.cancelQueries({
          queryKey: ["cart", cartToken, paymentMethodsIncludes],
        });
        queryClient.setQueryData(
          ["cart", cartToken, paymentMethodsIncludes],
          recalculated,
        );
      } catch (error) {
        console.error("Error recalculating cart after customer sync:", error);
        queryClient.invalidateQueries(
          { queryKey: ["cart", cartToken, paymentMethodsIncludes] },
          { cancelRefetch: false },
        );
      }
    },
    onError: async (error: unknown) => {
      if (isApiError(error) && error.status != null && error.status === 403) {
        try {
          await logoutCustomer({ cartToken });
        } catch {
          // Best-effort server logout; always clear client state
        }
        clearJwtCookies();
        window.location.reload();
      }
    },
  });

  const syncCustomerCallback = useEffectEvent(() => {
    if (jwt && !syncCustomerMutation.isPending) {
      syncCustomerMutation.mutate({ cartToken, jwt });
    }
  });

  useEffect(() => {
    if (!cartData) return;
    const cartEmail = cartData.cart?.email;
    const customerEmail = decodedJwt?.email;

    if (jwt && cartEmail !== customerEmail) {
      syncCustomerCallback();
    }
  }, [cartData, jwt, decodedJwt?.email]);
}
